import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap } from 'rxjs';

import { Comment as IssueComment } from '@ninety/platform/models/_shared/comment';
import { VoteType } from '@ninety/platform/models/_shared/vote-type';
import { SortDirection } from '@ninety/platform/models/enums/sort-direction';
import { Sorted } from '@ninety/platform/models/enums/sorted';
import { IntervalCode } from '@ninety/platform/models/issues/interval-code';
import { IssueMessageType } from '@ninety/platform/models/issues/issue-message-type';
import { IssueOptions } from '@ninety/platform/models/issues/issue-options.model';
import { IssueSortField } from '@ninety/platform/models/issues/issues-sort-field';
import { VoteItem } from '@ninety/platform/models/issues/vote-item';
import { RocksIssuesEvents, RealTimeActions, selectIsInMeeting, TeamSelectors } from '@ninety/platform/state';

import { Issue } from '../_shared/models/issue.model';
import { IssuesV2Service } from '../_shared/services/issues-v2.service';

import { IssuesActions, IssuesSubscriptionsActions } from './issues.actions';
import * as IssuesSelectors from './issues.selectors';

@Injectable()
export class IssuesSubscriptionsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly issuesV2Service = inject(IssuesV2Service);

  // Handle received new-issue messages from other users
  receiveIssueCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.new),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
      ]),
      filter(([, inMeeting, currentMeeting]) => inMeeting && !!currentMeeting),
      map(([{ message }]) => ({ issue: message.document as Issue })),
      concatLatestFrom(() => [
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(IssuesSelectors.selectIntervalCode),
        this.store.select(IssuesSelectors.selectArchiveFilter),
        this.store.select(IssuesSelectors.selectIsViewingArchivedVto),
      ]),
      filter(([{ issue }, currentTeamId, currentIntervalCode, showArchived, isViewingArchivedVto]) => {
        return (
          issue.teamId === currentTeamId &&
          issue.intervalCode === currentIntervalCode &&
          !showArchived &&
          !isViewingArchivedVto
        );
      }),
      map(([{ issue }]) => IssuesSubscriptionsActions.addIssueFromBroadcast({ issue }))
    )
  );

  receiveIssueUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.issue),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
      ]),
      filter(([, inMeeting, currentMeeting]) => inMeeting && !!currentMeeting),
      map(([{ message }, , , currentTeamId, isShortTerm]) => ({
        issue: message.document as Partial<Issue>,
        currentTeamId,
        isShortTerm,
      })),
      map(({ issue, currentTeamId, isShortTerm }) => {
        // If the broadcast includes a teamId update and it doesn't match the current filter, remove the issue
        if (issue.teamId && issue.teamId !== currentTeamId) {
          return IssuesSubscriptionsActions.deleteIssueFromBroadcast({ issueId: issue._id });
        }

        // If the broadcast includes an interval change that doesn't match the current filter, remove the issue
        if (issue.intervalCode) {
          const issueIsShortTerm = issue.intervalCode === IntervalCode.shortTerm;
          if (issueIsShortTerm !== isShortTerm) {
            return IssuesSubscriptionsActions.deleteIssueFromBroadcast({ issueId: issue._id });
          }
        }

        // If the broadcast includes an archived flag, archive the issue
        // this currently happens from PB where you can transform an issue into a rock and archive the issue
        if (issue.archived) {
          return IssuesSubscriptionsActions.archiveIssueFromBroadcast({ issueId: issue._id });
        }

        // Otherwise, update the issue normally
        return IssuesSubscriptionsActions.updateIssueFromBroadcast({ issue });
      })
    )
  );

  receiveIssueDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.delete),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
      ]),
      filter(([, inMeeting, currentMeeting]) => inMeeting && !!currentMeeting),
      map(([{ message }]) => {
        const issueId = message.document as string;
        return IssuesSubscriptionsActions.deleteIssueFromBroadcast({ issueId });
      })
    )
  );

  closeDetailViewOnBroadcastDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        IssuesSubscriptionsActions.deleteIssueFromBroadcast,
        IssuesSubscriptionsActions.removeReceivedIssueFromBroadcast
      ),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssueId)),
      filter(([{ issueId }, selectedIssueId]) => issueId === selectedIssueId),
      map(() => IssuesActions.closeDetailView())
    )
  );

  receiveIssueArchived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.archive),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(IssuesSelectors.selectArchiveFilter),
      ]),
      filter(([, inMeeting, currentMeeting, showArchived]) => inMeeting && !!currentMeeting && !showArchived),
      map(([{ message }]) => {
        const issueId = message.document as string;
        return IssuesSubscriptionsActions.archiveIssueFromBroadcast({ issueId });
      })
    )
  );

  closeDetailViewOnBroadcastArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesSubscriptionsActions.archiveIssueFromBroadcast),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssueId)),
      filter(([{ issueId }, selectedIssueId]) => issueId === selectedIssueId),
      map(() => IssuesActions.closeDetailView())
    )
  );

  receiveIssueUnarchived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.unarchive),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(IssuesSelectors.selectArchiveFilter),
      ]),
      filter(([, inMeeting, currentMeeting, showArchived]) => inMeeting && !!currentMeeting && showArchived),
      map(([{ message }]) => {
        const issueId = message.document as string;
        return IssuesSubscriptionsActions.unarchiveIssueFromBroadcast({ issueId });
      })
    )
  );

  closeDetailViewOnBroadcastUnarchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesSubscriptionsActions.unarchiveIssueFromBroadcast),
      concatLatestFrom(() => this.store.select(IssuesSelectors.selectSelectedIssueId)),
      filter(([{ issueId }, selectedIssueId]) => issueId === selectedIssueId),
      map(() => IssuesActions.closeDetailView())
    )
  );

  receiveIssueVoted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.vote),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
      ]),
      filter(([, inMeeting, currentMeeting]) => inMeeting && !!currentMeeting),
      map(([{ message, emitterId }]) => {
        const voteItem = message.document as VoteItem;
        const userId = voteItem.userId || (emitterId as string);
        return {
          voteItem,
          userId,
        };
      }),
      filter(({ voteItem, userId }) => !!userId && voteItem.itemType === 'issue'),
      map(({ voteItem, userId }) => {
        if (voteItem.voteType === VoteType.positive) {
          return IssuesSubscriptionsActions.addVoteFromBroadcast({
            issueId: voteItem._id,
            userId,
          });
        } else {
          return IssuesSubscriptionsActions.removeVoteFromBroadcast({
            issueId: voteItem._id,
            userId,
          });
        }
      })
    )
  );

  receiveIssueSortChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.sort),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
      ]),
      filter(([, inMeeting, currentMeeting]) => inMeeting && !!currentMeeting),
      map(([{ message }, , , isShortTerm, currentTeamId]) => ({
        payload: message.document as {
          listType?: string;
          field?: IssueSortField;
          order?: Sorted;
          isShortTerm?: boolean;
          teamId?: string; // new to V2
        },
        isShortTerm,
        currentTeamId,
      })),
      filter(({ payload, isShortTerm, currentTeamId }) => {
        return payload.listType === 'issue' && payload.isShortTerm === isShortTerm && payload.teamId === currentTeamId;
      }),
      map(({ payload }) => {
        const { field, order } = payload;
        const direction = order === Sorted.ascending ? SortDirection.ASC : SortDirection.DESC;

        return IssuesActions.setSortFromBroadcast({ field, direction });
      })
    )
  );

  receiveIssueSentBack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.sentBack),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(IssuesSelectors.selectArchiveFilter),
      ]),
      filter(([, inMeeting, currentMeeting, showArchived]) => inMeeting && !!currentMeeting && !showArchived),
      map(([{ message }, , currentMeeting]) => ({ issue: message.document as Issue, currentMeeting })),
      map(({ issue }) => IssuesSubscriptionsActions.removeReceivedIssueFromBroadcast({ issueId: issue._id }))
    )
  );

  receiveIssueSentToOtherTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.sentToOtherTeam),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectArchiveFilter),
      ]),
      filter(
        ([, inMeeting, currentMeeting, , isShortTerm, showArchived]) =>
          inMeeting && !!currentMeeting && isShortTerm && !showArchived
      ),
      map(([{ message }, , , currentTeamId]) => ({ issue: message.document as Issue, currentTeamId })),
      filter(({ issue, currentTeamId }) => issue.teamId === currentTeamId),
      switchMap(({ issue }) =>
        of(IssuesSubscriptionsActions.deleteIssueFromBroadcast({ issueId: issue._id }), IssuesActions.loadSentIssues())
      )
    )
  );

  receiveIssueRecalled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.recalled),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectArchiveFilter),
      ]),
      filter(
        ([, inMeeting, currentMeeting, , isShortTerm, showArchived]) =>
          inMeeting && !!currentMeeting && isShortTerm && !showArchived
      ),
      map(([{ message }, , , currentTeamId]) => ({ issue: message.document as Issue, currentTeamId })),
      filter(({ issue, currentTeamId }) => issue.teamId === currentTeamId),
      // Recall returns the issue to the sending team: refresh the sent panel (it drops off) and the team board (it reappears).
      switchMap(() => of(IssuesActions.loadSentIssues(), IssuesActions.loadTeamIssues()))
    )
  );

  receiveCommentDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.commentDelete),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
      ]),
      filter(([, inMeeting, currentMeeting]) => inMeeting && !!currentMeeting),
      map(([{ message }]) => {
        const { issueId, comment } = message.document as { issueId: string; comment: IssueComment };
        return IssuesSubscriptionsActions.deleteCommentFromBroadcast({ issueId, comment });
      })
    )
  );

  receiveCommentAddedOrUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.comment),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
      ]),
      filter(([, inMeeting, currentMeeting]) => inMeeting && !!currentMeeting),
      map(([{ message }]) => {
        const { issueId, comment } = message.document as { issueId: string; comment: IssueComment };
        return IssuesSubscriptionsActions.addOrUpdateCommentFromBroadcast({ issueId, comment });
      })
    )
  );

  receiveFetchObject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.fetch),
      filter(({ message }) => {
        const originalMessageType = message.originalMessageType;
        return originalMessageType === IssueMessageType.issue || originalMessageType === IssueMessageType.new;
      }),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
      ]),
      filter(([, inMeeting, currentMeeting]) => inMeeting && !!currentMeeting),
      map(([{ message }]) => {
        const issueId = (message.document as { _id: string })._id;
        const originalMessageType = message.originalMessageType as IssueMessageType;
        return IssuesSubscriptionsActions.fetchIssueFromBroadcast({ issueId, originalMessageType });
      })
    )
  );

  fetchIssueFromBroadcast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesSubscriptionsActions.fetchIssueFromBroadcast),
      switchMap(({ issueId, originalMessageType }) =>
        this.issuesV2Service.getIssue(issueId).pipe(
          map(issue => IssuesSubscriptionsActions.fetchIssueFromBroadcastSuccess({ issue, originalMessageType })),
          catchError((error: unknown) =>
            of(IssuesSubscriptionsActions.fetchIssueFromBroadcastFailure({ error, issueId, originalMessageType }))
          )
        )
      )
    )
  );

  handleFetchedIssueFromBroadcast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesSubscriptionsActions.fetchIssueFromBroadcastSuccess),
      map(({ issue, originalMessageType }) => {
        if (originalMessageType === IssueMessageType.new) {
          return IssuesSubscriptionsActions.addIssueFromBroadcast({ issue });
        } else {
          return IssuesSubscriptionsActions.updateIssueFromBroadcast({ issue });
        }
      })
    )
  );

  receivePbIssueDropped$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.pbDrop),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(IssuesSelectors.selectIsCurrentUserPresenter),
        this.store.select(IssuesSelectors.selectPlanningBoardPageView),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
      ]),
      filter(([{ message }, inMeeting, currentMeeting, isCurrentUserPresenter, isOnPlanningBoard, currentTeamId]) => {
        const payload = message.document as { teamId?: string };
        return (
          inMeeting &&
          !!currentMeeting &&
          !isCurrentUserPresenter &&
          isOnPlanningBoard &&
          !!payload.teamId &&
          !!currentTeamId &&
          payload.teamId === currentTeamId
        );
      }),
      map(([, , , , , teamId]) =>
        RocksIssuesEvents.getPlanningBoardIssues({ issueOptions: new IssueOptions({ teamId }) })
      )
    )
  );

  receivePbIssueSortChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === IssueMessageType.pbSort),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(IssuesSelectors.selectIsCurrentUserPresenter),
        this.store.select(IssuesSelectors.selectPlanningBoardPageView),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
      ]),
      filter(([{ message }, inMeeting, currentMeeting, isCurrentUserPresenter, isOnPlanningBoard, currentTeamId]) => {
        const payload = message.document as { teamId?: string };
        return (
          inMeeting &&
          !!currentMeeting &&
          !isCurrentUserPresenter &&
          isOnPlanningBoard &&
          !!payload.teamId &&
          !!currentTeamId &&
          payload.teamId === currentTeamId
        );
      }),
      map(([{ message }]) => {
        const payload = message.document as { field: IssueSortField; order: Sorted };
        const sortDirection = payload.order === Sorted.ascending ? SortDirection.ASC : SortDirection.DESC;
        return RocksIssuesEvents.sortIssuesLocalFromBroadcast({ sortField: payload.field, sortDirection });
      })
    )
  );

  receiveToggleVotingEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ message }) => message.messageType === 'toggle-issue-voting' && message.document === true),
      concatLatestFrom(() => [
        this.store.select(selectIsInMeeting),
        this.store.select(IssuesSelectors.selectCurrentMeeting),
        this.store.select(IssuesSelectors.selectTeamFilter),
        this.store.select(IssuesSelectors.selectCurrentMeetingTeamId),
        this.store.select(IssuesSelectors.selectArchiveFilter),
        this.store.select(IssuesSelectors.selectIntervalCode),
      ]),
      filter(
        ([, inMeeting, currentMeeting, teamId, currentMeetingTeamId, showArchived, intervalCode]) =>
          inMeeting &&
          !!currentMeeting &&
          !!teamId &&
          !!intervalCode &&
          teamId === currentMeetingTeamId &&
          !showArchived
      ),
      map(([, , , teamId, , , intervalCode]) => IssuesActions.loadUserVotes({ teamId, intervalCode }))
    )
  );
}
